import {
  ArticleType,
  ArticleResolution,
  KnownIssueResolutions,
} from '@wix/answers-api';
import { Badge, BadgeSkin, Tag, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent } from 'react';
import { DATA_HOOKS } from '../../../../../dataHooks';
import { launchedTagText } from '../../../../../utils/roadmap';
import classNames from 'classnames';
import css from './index.module.scss';

export type StatusTagProps = {
  type: ArticleType;
  status: ArticleResolution;
  resolutionEta?: Long | number | null;
};

const KnownIssueStatus: FunctionComponent<{
  status: ArticleResolution;
}> = ({ status }) => {
  const { t } = useTranslation();

  const skinMap: Record<KnownIssueResolutions, BadgeSkin> = {
    [KnownIssueResolutions.INVESTIGATING]: 'neutralDanger',
    [KnownIssueResolutions.THIRD_PARTY_BUG]: 'neutralDanger',
    [KnownIssueResolutions.RESOLVED]: 'neutralSuccess',
    [KnownIssueResolutions.WORKING_ON_IT]: 'warningLight',
  };

  return (
    <Badge
      dataHook={DATA_HOOKS.SEARCH_RESULT_STATUS_KNOWN_ISSUE}
      size="tiny"
      skin={skinMap[status as any as KnownIssueResolutions]}
    >
      {t(`known-issues-page.status.${status}`)}
    </Badge>
  );
};

const FeatureRequestStatus: FunctionComponent<{
  status: ArticleResolution;
  resolutionEta?: Long | number | null;
}> = ({ status, resolutionEta }) => {
  const { t } = useTranslation();

  const statusI18n =
    status === ArticleResolution.PENDING_RELEASE
      ? ArticleResolution.COMING_SOON
      : status;

  const statusText =
    status === ArticleResolution.RELEASED && resolutionEta
      ? launchedTagText(resolutionEta, t)
      : t(`roadmap.article-list.resolutions.${statusI18n}`);

  return (
    <Tag
      size="tiny"
      id={`${status}`}
      theme="warning"
      dataHook={DATA_HOOKS.SEARCH_RESULT_STATUS_FEATURE_REQUEST}
      removable={false}
      className={classNames(css.statusFR, {
        [css.collectingVotes]: ArticleResolution.COLLECTING_VOTES === status,
        [css.planned]: ArticleResolution.PLANNED === status,
        [css.inDev]: ArticleResolution.IN_DEVELOPMENT === status,
        [css.preLaunch]:
          ArticleResolution.COMING_SOON === status ||
          ArticleResolution.PENDING_RELEASE === status,
        [css.released]: ArticleResolution.RELEASED === status,
      })}
    >
      <Text className={css.statusText} size="tiny">
        {statusText}
      </Text>
    </Tag>
  );
};

export const StatusTag: FunctionComponent<StatusTagProps> = ({
  type,
  status,
  resolutionEta,
}) => {
  if (type === ArticleType.FEATURE_REQUEST) {
    return (
      <FeatureRequestStatus status={status} resolutionEta={resolutionEta} />
    );
  }
  if (type === ArticleType.KNOWN_ISSUE) {
    return <KnownIssueStatus status={status} />;
  }

  return null;
};
